import { groupBy } from 'lodash';
import { useMemo } from 'react';

import { useHistoricalPartitionData } from '@/api/historicalData/getPartitionDataHistory';
import { useHistoricalWaveData } from '@/api/historicalData/postHistoricalWaveData';
import {
    ConsolidatedWaveDataItem,
    PartitionWaveDataItemFormattedWithUnits,
    WaveDataItemFormattedWithUnits,
} from '@/api/historicalData/types';
import { isWaveSample, useOceansWaveData } from '@/api/oceansApi/useOceansWaveData';
import { convert } from '@/common/convert';
import { pickQueryStatus } from '@/common/react-query';
import { useOptionalSpotterContext, useSpotterContext } from '@/context/SpotterContext';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { DateRange } from '@/store';

const composeConsolidatedHistoricalData = (
    wavesData: WaveDataItemFormattedWithUnits[],
    partitionsData: PartitionWaveDataItemFormattedWithUnits[]
): ConsolidatedWaveDataItem[] => {
    if (!wavesData || !partitionsData) {
        return [];
    }

    const groupedByTimestampAndProcessing = groupBy(
        [...wavesData, ...partitionsData],
        (item) => `${item.timestamp}-${item.processing_source}`
    );

    const merged = Object.values(groupedByTimestampAndProcessing).map((group) => {
        return group.reduce(
            (acc, val) => ({
                ...acc,
                ...val,
            }),
            {}
        );
    });
    return merged as ConsolidatedWaveDataItem[];
};

export type UseWaveDataOptions = {
    dateRange: DateRange | null;
    config?: { enabled?: boolean };
};

export type UseConsolidatedDataOptions = UseWaveDataOptions & {
    spotterId?: string | null;
};

export type UseWaveDataResult = {
    isPending: boolean;
    isFetching: boolean;
    isError: boolean;
    hasPartitionData: boolean;
    data?: ConsolidatedWaveDataItem[];
};

/**
 * Primary entrypoint for SpotterContext-aware wave data
 *
 * Will use either oceans-api/wave-data or fetch/waves-history+partition-data-history as appropriate.
 *
 * @return Consolidated bulk and partition data, combined across processing sources (embedded filling in HDR gaps), in the user's preferred units.
 */
export const useWaveData = ({ config: { enabled = true } = {}, ...options }: UseWaveDataOptions): UseWaveDataResult => {
    const { spotterId, viaOceansApi } = useSpotterContext();
    const units = usePreferredUnits('distance');

    const fetchApiResult = useConsolidatedWaveData({
        ...options,
        spotterId,
        config: { enabled: enabled && !viaOceansApi },
    });
    const oceansApiResult = useOceansWaveData({
        ...options,
        spotterId,
        config: { enabled: enabled && viaOceansApi },
    });
    const oceansSamples = oceansApiResult.data;

    // one oceans-api request answers with the waves and the track together, and this is the waves half
    const processedOceansData = useMemo(() => {
        if (!viaOceansApi) return undefined;

        return oceansSamples?.filter(isWaveSample).map(({ series: _series, ...item }) => ({
            ...item,
            significantWaveHeight: convert(item.significantWaveHeight).from('m').to(units),
            significantWaveHeightUnits: units,
            significantWaveHeightUnitType: 'length',
        })) as ConsolidatedWaveDataItem[];
    }, [oceansSamples, units, viaOceansApi]);

    if (viaOceansApi) {
        return {
            ...pickQueryStatus(oceansApiResult),
            data: processedOceansData,
            hasPartitionData: false,
        };
    }

    return fetchApiResult;
};

export const useConsolidatedWaveData = ({
    dateRange,
    config: { enabled = true } = {},
    ...options
}: UseConsolidatedDataOptions) => {
    const { spotterId } = useOptionalSpotterContext(options);

    const {
        data: historicalWaves,
        isPending: isHistoricalWavesPending,
        isFetching: isHistoricalWavesFetching,
        isError: isHistoricalWavesError,
    } = useHistoricalWaveData({
        spotterId,
        dateRange,
        processingSources: 'all',
        config: { enabled },
    });
    const {
        data: partitionDataHistory,
        isPending: isPartitionDataPending,
        isFetching: isPartitionDataFetching,
        isError: isPartitionDataError,
    } = useHistoricalPartitionData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
        config: { enabled },
    });

    const data = useMemo(
        () =>
            historicalWaves && partitionDataHistory
                ? composeConsolidatedHistoricalData(historicalWaves, partitionDataHistory)
                : undefined,
        [historicalWaves, partitionDataHistory]
    );

    const isError = isHistoricalWavesError || isPartitionDataError;

    return {
        isPending: isHistoricalWavesPending || isPartitionDataPending,
        isFetching: isHistoricalWavesFetching || isPartitionDataFetching,
        isError,
        data,
        hasPartitionData: !!partitionDataHistory?.length,
    };
};
